/**
 * Trip history: local log merged with the server copy, per signed-in driver.
 *
 * A trip row used to be one line - distance, duration, vehicle - which was too
 * little to be worth keeping. Each trip now expands into what actually
 * happened: average and top speed, how long was spent moving versus stopped,
 * fuel estimate, how the drive compared with the estimate at departure, and a
 * segment-by-segment breakdown of the route with the road number and speed for
 * each few minutes of the drive.
 */

import { listTrips, deleteTrip } from '../core/db';
import { fetchTrips, syncTrip } from '../auth/api';
import type { Trip, TripSegment } from '../core/types';
import { formatClock, formatDateTime, formatDistance, formatDuration } from '../core/format';
import { store } from '../core/store';
import { t } from '../core/i18n';
import { analyseJourneys, type JourneyReport, type RouteVariant } from '../nav/routeHistory';
import { roadRefsFromStoredLabel } from '../nav/roads';

export class TripsPanel {
  readonly el: HTMLElement;
  private expanded = new Set<string>();
  private trips: Trip[] | null = null;
  /** Which journeys' detail is open in the report view. */
  private openReports = new Set<string>();
  /** The report view replaces the list in place rather than stacking a panel. */
  private view: 'list' | 'report' = 'list';

  constructor(
    parent: HTMLElement,
    private onClose: () => void,
    private onNavigateAgain: (trip: Trip) => void,
    /** Draw this trip's recorded track on the map, speed-coloured. */
    private onShowMap: (trip: Trip) => void,
  ) {
    this.el = document.createElement('div');
    this.el.className = 'panel panel--trips';
    this.el.hidden = true;
    parent.appendChild(this.el);
  }

  async open(): Promise<void> {
    this.el.hidden = false;
    this.trips = null;
    this.renderList();
    const local = await listTrips();
    let merged = local;
    try {
      const server = (await fetchTrips()).trips;
      const seen = new Set(local.map((x) => x.id));
      merged = [...local, ...server.filter((x) => !seen.has(x.id))].sort(
        (a, b) => b.startedAt - a.startedAt,
      ).slice(0, 100);

      /*
       * Re-upload trips the server holds only a stripped copy of.
       *
       * Until this was fixed the API discarded segments, speeds, moving time
       * and fuel on upload, so history opened on any other device had nothing
       * in it. The phone that recorded the drive still has the full record in
       * IndexedDB, so pushing those back fills the gap in place rather than
       * writing the detail off as lost.
       */
      const thin = new Map(server.map((x) => [x.id, x]));
      const toResend = local.filter((l) => {
        const remote = thin.get(l.id);
        if (!remote) return false;
        const localHas = (l.segments?.length ?? 0) > 0 || l.avgSpeed != null;
        const remoteHas = (remote.segments?.length ?? 0) > 0 || remote.avgSpeed != null;
        return localHas && !remoteHas;
      });
      // Bounded, and entirely best-effort: this is a repair, not a feature.
      for (const trip of toResend.slice(0, 25)) {
        void syncTrip(trip).catch(() => {});
      }
    } catch {
      /* offline: local only */
    }
    this.trips = merged;
    if (!this.el.hidden) this.renderList();
  }

  close(): void {
    this.el.hidden = true;
  }

  private renderList(): void {
    const units = store.get().settings.units;
    const trips = this.trips;
    const reports = trips ? analyseJourneys(trips) : [];

    const body =
      trips === null
        ? `<div class="panel__hint">${t('trips.loading')}</div>`
        : this.view === 'report'
          ? this.reportHtml(reports, units)
          : trips.length === 0
            ? `<div class="panel__hint">${t('trips.empty')}</div>`
            : trips.map((tr, i) => this.tripHtml(tr, i, units)).join('');

    /*
     * The report button only appears once there is something to report on -
     * an analysis link that opens "no repeated journeys yet" is a dead end the
     * driver learns to ignore.
     */
    const reportBtn = reports.length
      ? `<button class="icon-btn trips__report-btn ${this.view === 'report' ? 'is-on' : ''}"
                 data-act="report" aria-label="${t('report.title')}"
                 title="${t('report.title')}">${CHART_SVG}</button>`
      : '';

    this.el.innerHTML = `
      <div class="panel__bar">
        <button class="icon-btn" data-act="close" aria-label="Back">
          <svg viewBox="0 0 24 24"><path d="M15 4 L7 12 L15 20" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </button>
        <h2>${this.view === 'report' ? t('report.title') : t('trips.title')}</h2>
        ${reportBtn}
      </div>
      <div class="panel__scroll">${body}</div>`;

    this.el.querySelector('[data-act=close]')!.addEventListener('click', () => {
      // Back from the report returns to the list, not out of the panel.
      if (this.view === 'report') {
        this.view = 'list';
        this.renderList();
      } else this.onClose();
    });
    this.el.querySelector('[data-act=report]')?.addEventListener('click', () => {
      this.view = this.view === 'report' ? 'list' : 'report';
      this.renderList();
    });

    if (this.view === 'report') {
      this.el.querySelectorAll<HTMLElement>('[data-journey]').forEach((card) =>
        card.addEventListener('click', () => {
          const id = card.dataset.journey!;
          if (this.openReports.has(id)) this.openReports.delete(id);
          else this.openReports.add(id);
          this.renderList();
        }),
      );
      // "Drive it" on the winning corridor: replays that trip's route.
      this.el.querySelectorAll<HTMLButtonElement>('[data-drive]').forEach((b) =>
        b.addEventListener('click', (e) => {
          e.stopPropagation();
          const trip = this.trips?.find((x) => x.id === b.dataset.drive);
          if (trip) this.onNavigateAgain(trip);
        }),
      );
      return;
    }

    if (!trips) return;

    this.el.querySelectorAll<HTMLButtonElement>('[data-again]').forEach((b) =>
      b.addEventListener('click', () => this.onNavigateAgain(trips[Number(b.dataset.again)])),
    );
    this.el.querySelectorAll<HTMLButtonElement>('[data-map]').forEach((b) =>
      b.addEventListener('click', () => this.onShowMap(trips[Number(b.dataset.map)])),
    );
    this.el.querySelectorAll<HTMLButtonElement>('[data-del]').forEach((b) =>
      b.addEventListener('click', async () => {
        const tr = trips[Number(b.dataset.del)];
        await deleteTrip(tr.id);
        void this.open();
      }),
    );
    const toggle = (id: string) => {
      if (this.expanded.has(id)) this.expanded.delete(id);
      else this.expanded.add(id);
      this.renderList();
    };

    this.el.querySelectorAll<HTMLButtonElement>('[data-expand]').forEach((b) =>
      b.addEventListener('click', () => toggle(b.dataset.expand!)),
    );

    /*
     * The whole card opens it, not just the small "details" button.
     *
     * Reported as "clicking one does nothing": the only click targets were
     * three small buttons at the bottom of the card, and the obvious thing to
     * click - the trip itself - was inert. Clicks inside the action row are
     * left alone so Again and Delete still do their own jobs.
     */
    this.el.querySelectorAll<HTMLElement>('[data-card]').forEach((card) => {
      card.addEventListener('click', (e) => {
        if ((e.target as HTMLElement).closest('.trip__actions')) return;
        toggle(card.dataset.card!);
      });
      card.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        toggle(card.dataset.card!);
      });
    });
  }

  private tripHtml(tr: Trip, i: number, units: 'metric' | 'imperial'): string {
    const open = this.expanded.has(tr.id);
    const speedUnit = units === 'imperial' ? 'mph' : 'km/h';
    const conv = (kmh: number) => (units === 'imperial' ? Math.round(kmh / 1.609344) : kmh);

    const stoppedSeconds =
      tr.movingSeconds != null ? Math.max(0, tr.duration - tr.movingSeconds) : null;

    // How the drive compared with what the router promised at departure.
    let vsEstimate = '';
    if (tr.predictedDuration && tr.duration) {
      const diff = tr.duration - tr.predictedDuration;
      const sign = diff >= 0 ? '+' : '-';
      const cls = diff > 120 ? 'is-slower' : diff < -120 ? 'is-faster' : '';
      vsEstimate = `<span class="trip-stat ${cls}">${t('trips.vsEstimate')}
        <strong>${sign}${formatDuration(Math.abs(diff))}</strong></span>`;
    }

    const stats = [
      tr.avgSpeed
        ? `<span class="trip-stat">${t('trips.avgSpeed')} <strong>${conv(tr.avgSpeed)} ${speedUnit}</strong></span>`
        : '',
      tr.maxSpeed
        ? `<span class="trip-stat">${t('trips.maxSpeed')} <strong>${conv(tr.maxSpeed)} ${speedUnit}</strong></span>`
        : '',
      tr.movingSeconds != null
        ? `<span class="trip-stat">${t('trips.moving')} <strong>${formatDuration(tr.movingSeconds)}</strong></span>`
        : '',
      stoppedSeconds != null && stoppedSeconds > 30
        ? `<span class="trip-stat">${t('trips.stopped')} <strong>${formatDuration(stoppedSeconds)}</strong></span>`
        : '',
      tr.fuelEstimate
        ? `<span class="trip-stat">${t('trips.fuel')} <strong>${tr.fuelEstimate} L</strong></span>`
        : '',
      tr.trafficSignals
        ? `<span class="trip-stat">${t('stats.signals', { n: tr.trafficSignals })}</span>`
        : '',
      tr.reroutes ? `<span class="trip-stat">${t('trips.reroutes', { n: tr.reroutes })}</span>` : '',
      vsEstimate,
    ]
      .filter(Boolean)
      .join('');

    const segments = (tr.segments ?? []).length
      ? `<div class="trip-segments">
           <div class="trip-segments__title">${t('trips.segments')}</div>
           ${tr.segments!.map((s) => this.segmentHtml(s, units, speedUnit, conv)).join('')}
         </div>`
      : '';

    return `
      <div class="trip ${open ? 'is-open' : ''}" data-i="${i}" data-card="${escapeHtml(tr.id)}"
           role="button" tabindex="0" aria-expanded="${open}">
        <div class="trip__head">
          <strong>${escapeHtml(tr.destinationName)}</strong>
          <span>${formatDateTime(tr.startedAt)}</span>
        </div>
        <div class="trip__meta">
          ${formatDistance(tr.distance, units)} · ${formatDuration(tr.duration)} · ${escapeHtml(tr.vehicleId)}
        </div>
        ${
          open
            ? stats || segments
              ? `<div class="trip-stats">${stats}</div>${segments}`
              : `<div class="trip-stats trip-stats--empty">${t('trips.noDetail')}</div>`
            : ''
        }
        <div class="trip__actions">
          <button class="btn btn--small btn--ghost" data-expand="${escapeHtml(tr.id)}">
            ${open ? '▴' : '▾'} ${t('trips.details')}
          </button>
          ${
            (tr.track?.length ?? 0) >= 2
              ? `<button class="btn btn--small btn--ghost" data-map="${i}">${t('trips.map')}</button>`
              : ''
          }
          <button class="btn btn--small btn--ghost" data-again="${i}">${t('trips.again')}</button>
          <button class="btn btn--small btn--ghost" data-del="${i}">${t('trips.delete')}</button>
        </div>
      </div>`;
  }

  /**
   * The analysis view: journeys driven more than once, and which way was
   * quicker.
   *
   * A verdict is only stated when the evidence supports it (`confident`) -
   * two corridors, each driven at least twice. Below that the same numbers
   * are shown as history, without a recommendation, because one lucky run at
   * dawn is not a better road.
   */
  private reportHtml(reports: JourneyReport[], units: 'metric' | 'imperial'): string {
    if (!reports.length) return `<div class="panel__hint">${t('report.empty')}</div>`;

    return reports
      .map((r) => {
        const open = this.openReports.has(r.id);
        const saving = r.confident && r.savingSeconds > 60 ? Math.round(r.savingSeconds / 60) : 0;

        const verdict = saving
          ? `<div class="jr__verdict">${t('report.saves', {
              road: escapeHtml(r.best?.roads[0] ?? '—'),
              mins: String(saving),
            })}</div>`
          : `<div class="jr__verdict jr__verdict--soft">${
              r.variants.length > 1 ? t('report.tooClose') : t('report.oneWay')
            }</div>`;

        return `
      <div class="jr ${open ? 'is-open' : ''}" data-journey="${escapeHtml(r.id)}"
           role="button" tabindex="0" aria-expanded="${open}">
        <div class="jr__head">
          <strong>${escapeHtml(r.destinationName)}</strong>
          <span class="jr__count">${t('report.drives', { n: r.trips.length })}</span>
        </div>
        <div class="jr__sub">${escapeHtml(r.originName)} → ${escapeHtml(r.destinationName)}</div>
        ${verdict}
        ${open ? r.variants.map((v) => this.variantHtml(v, r, units)).join('') : ''}
      </div>`;
      })
      .join('');
  }

  /** One corridor's row in the report: how it was driven, and how it compares. */
  private variantHtml(
    v: RouteVariant,
    r: JourneyReport,
    units: 'metric' | 'imperial',
  ): string {
    const speedUnit = units === 'imperial' ? 'mph' : 'km/h';
    const conv = (kmh: number) => (units === 'imperial' ? Math.round(kmh / 1.609344) : kmh);
    const isBest = r.confident && v.id === r.best?.id;
    // Difference against the fastest corridor, so every row is comparable.
    const delta = v.medianDuration - (r.variants[0]?.medianDuration ?? v.medianDuration);

    const bits = [
      v.avgSpeed != null
        ? `<span class="jr-stat">${t('trips.avgSpeed')} <strong>${conv(Math.round(v.avgSpeed))} ${speedUnit}</strong></span>`
        : '',
      v.avgSignals != null && v.avgSignals >= 1
        ? `<span class="jr-stat">${t('stats.signals', { n: Math.round(v.avgSignals) })}</span>`
        : '',
      v.avgStopped != null && v.avgStopped > 30
        ? `<span class="jr-stat">${t('trips.stopped')} <strong>${formatDuration(Math.round(v.avgStopped))}</strong></span>`
        : '',
    ]
      .filter(Boolean)
      .join('');

    return `
      <div class="jr-var ${isBest ? 'is-best' : ''}">
        <div class="jr-var__head">
          <span class="jr-var__roads">${escapeHtml(v.roads.join(' · ') || t('trips.unknownRoad'))}</span>
          <span class="jr-var__time">${formatDuration(v.medianDuration)}${
            delta > 60 ? ` <small>+${formatDuration(delta)}</small>` : ''
          }</span>
        </div>
        <div class="jr-var__meta">
          ${t('report.timesDriven', { n: v.trips.length })} ·
          ${formatDistance(v.medianDistance, units)} ·
          ${t('report.last')} ${formatDateTime(v.lastDrivenAt)}
        </div>
        ${bits ? `<div class="jr-stats">${bits}</div>` : ''}
        <div class="jr-var__actions">
          <button class="btn btn--small btn--ghost" data-drive="${escapeHtml(v.trips[0].id)}">
            ${t('report.driveThis')}
          </button>
        </div>
      </div>`;
  }

  private segmentHtml(
    s: TripSegment,
    units: 'metric' | 'imperial',
    speedUnit: string,
    conv: (kmh: number) => number,
  ): string {
    const clock = formatClock;
    const road = roadRefsFromStoredLabel(s.road ?? '').join(' / ');
    return `
      <div class="trip-seg">
        <span class="trip-seg__time">${clock(s.startedAt)}</span>
        <span class="trip-seg__road">${escapeHtml(road || t('trips.unknownRoad'))}</span>
        <span class="trip-seg__num">${formatDistance(s.distance, units)}</span>
        <span class="trip-seg__num">${conv(s.avgSpeed)}<small>/${conv(s.maxSpeed)} ${speedUnit}</small></span>
        ${
          s.stoppedSeconds > 20
            ? `<span class="trip-seg__stop">■ ${formatDuration(s.stoppedSeconds)}</span>`
            : '<span class="trip-seg__stop"></span>'
        }
      </div>`;
  }
}

/** Bar-chart glyph: the link from the trip list into the route analysis. */
const CHART_SVG = `<svg viewBox="0 0 24 24"><path d="M4 20 V10 M10 20 V4 M16 20 v-7 M22 20 H2" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/></svg>`;

function escapeHtml(value: unknown): string {
  // Persisted trips predate some required fields in the reconstructed type.
  // A missing legacy label must not strand the entire panel on "Loading…".
  return String(value ?? '').replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}
